import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import {
  toNextPaymentDue,
  toStatementPayment,
  toStatementSummary,
} from 'src/page-components/Accounts/AccountStatementsTab/accountStatements.mapping'
import {
  ACCOUNT_STATEMENTS_DEMO,
  type AccountStatementsData,
} from 'src/page-components/Accounts/AccountStatementsTab/accountStatementsDemoData'
import {
  type CardManagementSource,
  isUnprovisioned,
  resolveCardManagementSource,
} from 'src/page-components/Accounts/cardManagementSource'
import { isCardManagementActionAvailable } from 'src/queries/physicalCardManagement/physicalCardManagement.models'
import { physicalCardManagementQueries } from 'src/queries/physicalCardManagement/physicalCardManagement.queries'

/**
 * Single data seam for the card-detail Statements tab (EX-40600).
 *
 * Mirrors `useCardDetailHeroData` for the sibling Card Management surface, and
 * carries the same rule: section components MUST read through this hook rather
 * than importing `ACCOUNT_STATEMENTS_DEMO` directly.
 *
 * ## Real data, with the demo figures as a per-group fallback
 *
 * Four endpoints back this tab, across two frozen contracts:
 *
 * - `GET /creditcards/{id}/cardmanagement/capabilities` — independent read gates
 * - `GET /creditcards/{id}/cardmanagement/payments/summary` — next amount due
 * - `GET /creditcards/{id}/cardmanagement/payments` — payment history
 * - `GET /creditcards/{id}/cardmanagement/statements` — closed statements
 *
 * (Statement LINE ITEMS are a fifth route, fetched lazily per statement when a
 * detail modal opens — see `useStatementTransactions`. Loading them here would
 * cost one request per row on tab load for ledgers almost nobody opens.)
 *
 * The return groups by SOURCE, not by consumer, and that grouping is what makes
 * the fallback safe to apply piecemeal: `payments` and `statements` are
 * separately fetched, so each falls back on its own and a statements failure
 * cannot swap out figures the payments endpoint answered correctly. Sections
 * destructure only the group they use.
 *
 * ## When the fallback applies
 *
 * Per group, via the shared `resolveCardManagementSource` — the same rule the
 * Activity tab uses, so the two tabs on this page cannot drift apart on it:
 *
 * - `loading` while the reads are in flight. NOT demo: painting fabricated
 *   balances for a frame and replacing them is how invented figures come to
 *   look real.
 * - `demo` when an allowed read fails as unprovisioned (`403` not enrolled /
 *   `404` no route or card).
 * - `absent` when the available reads settle without anything renderable. This
 *   includes issuer-empty responses and cards for which none of the independent
 *   read capabilities is available.
 * - `live` when any independently authorized endpoint supplies renderable data.
 * - `error` for a genuine failure, with a retry. A `503` is a transient
 *   outage; substituting fabricated figures for it would misreport the card as
 *   known-and-empty.
 *
 * Successful mock responses carry `X-Extend-Card-Management-Mock: true`. A 200
 * is not by itself evidence of an authoritative statement ledger.
 *
 * WHEN A GROUP IS ON ITS FALLBACK, ITS FIGURES ARE FABRICATED — the tab badges
 * each section independently, because an invented balance presented as a real
 * one is the specific failure this tab has to avoid. Keep the exposure in mind
 * when changing the gates: this tab is opened by the server capability
 * `READ_PHYSICAL_CARD_STATEMENTS` as well as by a client flag (see
 * `isUpdatedCardStatementsEnabled`), so enrolling an organization on the
 * backend is by itself enough to reach it.
 */

/**
 * How many statements the list shows before "See more statements".
 *
 * The wire has no `recent`/`older` split — that division is purely this tab's
 * progressive-disclosure affordance, so it is applied to the fetched page here
 * rather than being asked of the server. Matches the demo's four.
 */
const RECENT_STATEMENT_COUNT = 4

/**
 * One page is requested, and the tab does not paginate further.
 *
 * "See more statements" reveals the already-fetched remainder in place; it is
 * not a load-more control, and wiring it to the cursor would change a
 * zero-latency disclosure into a network round trip. 20 is the contract's
 * default page size and comfortably more than the eight the design shows.
 */
const STATEMENT_PAGE_LIMIT = 20

/** Payment history is a short list in the design; one page is the whole section. */
const PAYMENT_PAGE_LIMIT = 20

export interface AccountStatementsResult
  extends Omit<AccountStatementsData, 'payments' | 'statements'> {
  /**
   * The payment figures, or `null` when there are none to render — loading,
   * error, or `absent`. Nullable rather than always-populated so a consumer
   * cannot read fabricated figures by forgetting to check `paymentsState`
   * first; see the return statement for why EX-40857 required that.
   */
  payments: AccountStatementsData['payments'] | null
  /** Same, for the statement list. */
  statements: AccountStatementsData['statements'] | null
  /**
   * What the `payments` group above actually is. `demo` while the fallback is
   * showing, `loading` until the reads resolve — the tab renders a loading
   * state rather than demo figures it is about to replace, which previously
   * flashed fabricated balances for a frame on every open.
   */
  paymentsState: CardManagementSource
  /** Same, for `statements`. The two resolve independently. */
  statementsState: CardManagementSource
  /** Re-requests the payment summary and history. */
  retryPayments: () => void
  /** Re-requests the statement list. */
  retryStatements: () => void
}

/**
 * @param creditCardId - The card whose statements to read. `undefined` while
 *   the card is still loading; the queries stay disabled until it resolves.
 * @param options.refetchOnMount - Whether THIS consumer needs current figures
 *   on every mount. See {@link freshnessFor}.
 */
export const useAccountStatementsData = (
  creditCardId: string | undefined,
  { refetchOnMount = false }: { refetchOnMount?: boolean } = {},
): AccountStatementsResult => {
  /*
    The app's query client defaults `refetchOnMount` to
    `(query) => query.observers.length <= 1` (src/queryClient.ts), so a query
    with a second observer already attached does NOT refetch when a component
    mounts. That default exists to stop two components on one page each firing
    the same request.

    EX-40857 made AccountEntityPage a PERMANENT observer of these three queries,
    because it has to know whether the Statements tab has anything to carry
    before the tab exists. Without this override that permanent observer would
    silently disable the tab's own mount refetch: React Query adds the mounting
    observer BEFORE evaluating `refetchOnMount` (query-core `onSubscribe`), so
    the tab would always see a count of 2 and re-entering it would render
    whatever loaded on first page render for the rest of the session.

    So the two consumers ask for different things, which is why this is a
    parameter rather than a query-level setting: the tab RENDERS these figures
    and opts in; the page only probes for availability and stays passive on the
    client default. Absent (not `undefined`) when not requested — an explicit
    `refetchOnMount: undefined` would override the client default rather than
    inherit it.
  */
  const freshnessFor = refetchOnMount
    ? ({ refetchOnMount: 'always' } as const)
    : {}

  const capabilitiesQuery = useQuery(
    physicalCardManagementQueries.capabilities(creditCardId),
  )
  const capabilities = capabilitiesQuery.data?.capabilities
  const summaryAllowed = capabilitiesQuery.data
    ? isCardManagementActionAvailable(
        capabilities,
        'READ_PHYSICAL_CARD_PAYMENT_SUMMARY',
      )
    : undefined
  const paymentsAllowed = capabilitiesQuery.data
    ? isCardManagementActionAvailable(
        capabilities,
        'READ_PHYSICAL_CARD_PAYMENTS',
      )
    : undefined
  const statementsAllowed = capabilitiesQuery.data
    ? isCardManagementActionAvailable(
        capabilities,
        'READ_PHYSICAL_CARD_STATEMENTS',
      )
    : undefined

  const summaryQuery = useQuery({
    ...physicalCardManagementQueries.paymentSummary(creditCardId),
    ...freshnessFor,
    enabled: Boolean(creditCardId) && summaryAllowed === true,
  })
  const paymentsQuery = useQuery({
    ...physicalCardManagementQueries.payments({
      creditCardId,
      limit: PAYMENT_PAGE_LIMIT,
    }),
    ...freshnessFor,
    enabled: Boolean(creditCardId) && paymentsAllowed === true,
  })
  const statementsQuery = useQuery({
    ...physicalCardManagementQueries.statements({
      creditCardId,
      limit: STATEMENT_PAGE_LIMIT,
    }),
    ...freshnessFor,
    enabled: Boolean(creditCardId) && statementsAllowed === true,
  })

  const summary = summaryQuery.data
  const paymentItems = paymentsQuery.data?.items
  const statementItems = statementsQuery.data?.items

  const payments = useMemo(() => {
    if (summary === undefined && paymentItems === undefined) {
      return null
    }

    const nextPaymentDue =
      summary !== undefined
        ? toNextPaymentDue(summary.nextPaymentDue)
        : undefined
    const history = paymentItems?.map(toStatementPayment)

    /*
     * The tile is live when EITHER endpoint supplied something renderable.
     * `undefined` still records that an independently gated endpoint never
     * answered, while `null`/`[]` record successful but empty reads. Under the
     * FIS absence rules neither empty shape is strong enough to make a customer
     * claim, so the tile is absent only when both renderable halves are empty.
     */
    const hasNextPayment =
      nextPaymentDue !== null && nextPaymentDue !== undefined
    const hasPaymentHistory = Boolean(history?.length)

    return hasNextPayment || hasPaymentHistory
      ? { nextPaymentDue, history }
      : null
  }, [summary, paymentItems])

  const statements = useMemo(() => {
    if (!statementItems) {
      return null
    }
    // A cursor counts as data even with no items on this page: `nextCursor` is
    // the contract's sole terminal signal and a non-final page may legitimately
    // be empty. Fabricating over a response that says more exists is the wrong
    // way to be wrong, even though this tab reads only one page.
    if (statementItems.length === 0 && !statementsQuery.data?.nextCursor) {
      return null
    }

    const mapped = statementItems.map(toStatementSummary)

    return {
      recent: mapped.slice(0, RECENT_STATEMENT_COUNT),
      older: mapped.slice(RECENT_STATEMENT_COUNT),
    }
  }, [statementItems, statementsQuery.data?.nextCursor])

  // Grouped by SOURCE, so each resolves on its own: a statements outage cannot
  // blank or fabricate payment figures the payments endpoints answered.
  const paymentsState = resolveCardManagementSource({
    isLoading:
      !creditCardId ||
      (!capabilitiesQuery.data && !capabilitiesQuery.isError) ||
      (summaryAllowed === true && summaryQuery.isLoading) ||
      (paymentsAllowed === true && paymentsQuery.isLoading),
    isError:
      capabilitiesQuery.isError ||
      (summaryAllowed === true && summaryQuery.isError) ||
      (paymentsAllowed === true && paymentsQuery.isError),
    // A GENUINE failure from either half outranks an unprovisioned answer from
    // the other. `??` alone let summary's verdict win whenever both failed, so
    // an unprovisioned summary (`403`) paired with a real outage on payments
    // (`503`) resolved to `demo` — fabricated figures rendered over an outage,
    // which is precisely what `resolveCardManagementSource` refuses to do,
    // reached by a different route.
    error:
      [
        capabilitiesQuery.error,
        summaryAllowed === true ? summaryQuery.error : null,
        paymentsAllowed === true ? paymentsQuery.error : null,
      ].find((queryError) => queryError && !isUnprovisioned(queryError)) ??
      capabilitiesQuery.error ??
      (summaryAllowed === true ? summaryQuery.error : null) ??
      (paymentsAllowed === true ? paymentsQuery.error : null),
    hasData: payments !== null,
  })

  const statementsState = resolveCardManagementSource({
    isLoading:
      !creditCardId ||
      (!capabilitiesQuery.data && !capabilitiesQuery.isError) ||
      (statementsAllowed === true && statementsQuery.isLoading),
    isError:
      capabilitiesQuery.isError ||
      (statementsAllowed === true && statementsQuery.isError),
    error:
      capabilitiesQuery.error ??
      (statementsAllowed === true ? statementsQuery.error : null),
    hasData: statements !== null,
  })

  // Destructured because React Query returns a NEW result object every render.
  // Closing over the queries themselves would give these callbacks a fresh
  // identity on every render and make the `useCallback`s inert; `refetch` is
  // bound to the observer and stable. Same fix as the Activity hook.
  const { refetch: refetchCapabilities } = capabilitiesQuery
  const { refetch: refetchSummary } = summaryQuery
  const { refetch: refetchPayments } = paymentsQuery
  const { refetch: refetchStatements } = statementsQuery

  const retryPayments = useCallback(() => {
    if (capabilitiesQuery.isError) {
      void refetchCapabilities()
      return
    }
    if (summaryAllowed === true) void refetchSummary()
    if (paymentsAllowed === true) void refetchPayments()
  }, [
    capabilitiesQuery.isError,
    paymentsAllowed,
    refetchCapabilities,
    refetchPayments,
    refetchSummary,
    summaryAllowed,
  ])

  const retryStatements = useCallback(() => {
    if (capabilitiesQuery.isError) {
      void refetchCapabilities()
      return
    }
    if (statementsAllowed === true) void refetchStatements()
  }, [
    capabilitiesQuery.isError,
    refetchCapabilities,
    refetchStatements,
    statementsAllowed,
  ])

  return {
    /*
      The demo payload is handed back for the `demo` state and NOTHING else.

      This used to be `payments ?? ACCOUNT_STATEMENTS_DEMO.payments` — always
      populated, with the state alone saying whether the figures were real.
      That was safe while every empty read WAS the demo case. EX-40857 split
      `absent` out of `demo`, and substituting fabricated figures for a card the
      bank network genuinely has nothing for is exactly what this ticket
      removes: a consumer that reads the payload without first checking the
      state would render invented money for an empty card.

      `null` for loading, error and absent forces that check at the type level
      rather than trusting each caller to remember it.
    */
    payments:
      paymentsState === 'demo' ? ACCOUNT_STATEMENTS_DEMO.payments : payments,
    statements:
      statementsState === 'demo'
        ? ACCOUNT_STATEMENTS_DEMO.statements
        : statements,
    paymentsState,
    statementsState,
    retryPayments,
    retryStatements,
  }
}
